import { rem } from 'polished'
import React, { useCallback, useState } from 'react'
import styled from 'styled-components'

import TextButton from 'components/Luxkit/Button/TextButton'
import Dropdown from 'components/Luxkit/Dropdown/Dropdown'
import Group from 'components/utils/Group'
import DateRangePicker from 'tripPlanner/components/TripModal/Common/DateRangePicker'

const Container = styled.div`
  max-width: calc(100vw - ${rem(32)});
  box-shadow: ${(props) => props.theme.shadow.bottom.small};
  border: 1px solid ${(props) => props.theme.palette.neutral.six};
  border-radius: ${(props) => props.theme.borderRadius.S};
  background-color: ${(props) => props.theme.palette.neutral.eight};
  min-width: ${rem(420)};
  padding: ${rem(20)};
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`

interface Props {
  triggerRef: React.RefObject<HTMLElement>
  isOpen: boolean
  initialDate?: moment.Moment
  onSelect: (startDate: moment.Moment, endDate: moment.Moment) => void
  onClear?: () => void
  onClose: () => void
}

function TripDatesDropdown({
  triggerRef,
  isOpen,
  initialDate,
  onSelect,
  onClear,
  onClose,
}: Props) {
  const [selectedStartDate, setSelectedStartDate] = useState<
    moment.Moment | undefined
  >()

  const onSelectEndDate = useCallback(
    (endDate?: moment.Moment) => {
      if (selectedStartDate && endDate) {
        setSelectedStartDate(undefined)
        onSelect(selectedStartDate, endDate)
      }
    },
    [onSelect, selectedStartDate],
  )

  const onRequestClose = useCallback(() => {
    setSelectedStartDate(undefined)
    onClose()
  }, [onClose])

  const onClickClear = useCallback(() => {
    setSelectedStartDate(undefined)
    onClear?.()
  }, [onClear])

  return (
    <Dropdown
      triggerRef={triggerRef}
      open={isOpen}
      onClose={onRequestClose}
      position="bottom-start"
    >
      <Container>
        <DateRangePicker
          onChangeStartDate={setSelectedStartDate}
          onChangeEndDate={onSelectEndDate}
          startDate={selectedStartDate}
          initDate={initialDate}
          allowSameDay
        />
        {!!onClear && (
          <Group direction="horizontal" horizontalAlign="end">
            <TextButton kind="tertiary" onClick={onClickClear} outdent="right">
              Clear dates
            </TextButton>
          </Group>
        )}
      </Container>
    </Dropdown>
  )
}

export default TripDatesDropdown
